import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';
import appConfig from '../config';

interface Currency {
  id: number;
  code: string;
  name: string;
  currency_type: string;
}

export default function WithdrawalGateways() {
  const [gateways, setGateways] = useState<any[]>([]);
  const [activeGatewayId, setActiveGatewayId] = useState<number>(0);
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [editData, setEditData] = useState<any>(null);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [curSearch, setCurSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const emptyGateway = {
    gateway_name: 'manual_upi_wallet',
    display_name: '',
    currency: '',
    min_amount: 0,
    max_amount: 0,
    api_key: '',
    secret_key: '',
    instructions: '',
    extra_config: '{}',
    logo_url: '',
    status: true,
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCurrencies = async () => {
    try {
      const res = await apiClient.get('/api/currencies');
      if (res.data?.success) setAllCurrencies(res.data.currencies || []);
    } catch { /* silent */ }
  };

  const fetchGateways = async () => {
    try {
      const res = await apiClient.get('/admin/withdrawal-gateways', {
        headers: { authorization: localStorage.getItem('token') }
      });
      if (res.data?.success) {
        setGateways(res.data.data);
        if (res.data.data.length > 0 && activeGatewayId === 0 && !isAddingNew) {
          setActiveGatewayId(res.data.data[0].id);
          setEditData(res.data.data[0]);
          setSelectedCurrencies((res.data.data[0].currency || '').split(',').map((c: string) => c.trim()).filter(Boolean));
        } else if (isAddingNew) {
          // Keep current edit state
        } else {
          const updated = res.data.data.find((g: any) => g.id === activeGatewayId);
          if (updated) {
            setEditData(updated);
            setSelectedCurrencies((updated.currency || '').split(',').map((c: string) => c.trim()).filter(Boolean));
          } else if (res.data.data.length > 0) {
            setActiveGatewayId(res.data.data[0].id);
            setEditData(res.data.data[0]);
            setSelectedCurrencies((res.data.data[0].currency || '').split(',').map((c: string) => c.trim()).filter(Boolean));
          }
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error fetching withdrawal gateways');
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchGateways();
  }, []);

  const handleTabClick = (gateway: any) => {
    setIsAddingNew(false);
    setActiveGatewayId(gateway.id);
    setEditData({ ...gateway });
    setSelectedCurrencies((gateway.currency || '').split(',').map((c: string) => c.trim()).filter(Boolean));
  };

  const handleAddNewClick = () => {
    setIsAddingNew(true);
    setActiveGatewayId(0);
    setEditData({ ...emptyGateway });
    setSelectedCurrencies([]);
  };

  const toggleCurrency = (code: string) => {
    setSelectedCurrencies(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const getExtraConfigValue = (key: string) => {
    try {
      const config = typeof editData.extra_config === 'string' 
        ? JSON.parse(editData.extra_config || '{}') 
        : (editData.extra_config || {});
      return config[key] || '';
    } catch {
      return '';
    }
  };

  const getCustomFields = (): { label: string; key: string; type: string }[] => {
    try {
      const config = typeof editData?.extra_config === 'string' 
        ? JSON.parse(editData.extra_config || '{}') 
        : (editData?.extra_config || {});
      
      if (Array.isArray(config.custom_fields)) {
        return config.custom_fields;
      }
      
      // Default fallback based on type
      if (editData?.gateway_name === 'manual_bank') {
        return [
          { label: 'Bank Name', key: 'bank_name', type: 'text' },
          { label: 'Account Holder Name', key: 'account_holder', type: 'text' },
          { label: 'Account Number', key: 'account_number', type: 'number' },
          { label: 'IFSC Code', key: 'ifsc', type: 'text' }
        ];
      } else {
        return [
          { label: 'UPI ID / Wallet Address', key: 'address', type: 'text' }
        ];
      }
    } catch {
      return [];
    }
  };

  const setCustomFields = (fields: { label: string; key: string; type: string }[]) => {
    try {
      const config = typeof editData.extra_config === 'string' 
        ? JSON.parse(editData.extra_config || '{}') 
        : (editData.extra_config || {});
      config.custom_fields = fields;
      setEditData({ ...editData, extra_config: config });
    } catch {}
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const currencyString = selectedCurrencies.join(',');
    const formData = new FormData();

    const extraConfigStr = typeof editData.extra_config === 'object' 
      ? JSON.stringify(editData.extra_config) 
      : editData.extra_config;

    const dataToSend = { ...editData, currency: currencyString, extra_config: extraConfigStr };

    Object.keys(dataToSend).forEach(key => {
      if (key !== 'logo' && dataToSend[key] !== null) {
        formData.append(key, typeof dataToSend[key] === 'object' ? JSON.stringify(dataToSend[key]) : dataToSend[key]);
      }
    });

    if (editData.logo instanceof File) formData.append('logo', editData.logo);

    try {
      let res;
      if (isAddingNew) {
        res = await apiClient.post('/admin/withdrawal-gateways', formData, {
          headers: { authorization: localStorage.getItem('token'), 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await apiClient.put(`/admin/withdrawal-gateways/${activeGatewayId}`, formData, {
          headers: { authorization: localStorage.getItem('token'), 'Content-Type': 'multipart/form-data' }
        });
      }

      if (res.data?.success) {
        toast.success(isAddingNew ? 'Gateway created successfully' : 'Gateway updated successfully');
        setIsAddingNew(false);
        if (isAddingNew && res.data.data?.id) {
          setActiveGatewayId(res.data.data.id);
        }
        fetchGateways();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error saving gateway');
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this withdrawal gateway?")) {
      try {
        const res = await apiClient.delete(`/admin/withdrawal-gateways/${activeGatewayId}`, {
          headers: { authorization: localStorage.getItem('token') }
        });
        if (res.data?.success) {
          toast.success("Gateway deleted successfully");
          setActiveGatewayId(0);
          fetchGateways();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Error deleting gateway');
      }
    }
  };

  return (
    <div className="text-white">
      <div className="mb-4">
        <h3 className="text-lg font-bold mb-2">Withdrawal Methods</h3>
        <p className="text-sm text-gray-400">Configure limits and settings for withdrawal methods.</p>
      </div>
      
      {/* Horizontal Tabs */}
      <div className="flex flex-nowrap gap-2 mb-6 border-b border-gray-700 pb-2 overflow-x-auto whitespace-nowrap">
        {gateways.map(g => (
          <button
            key={g.id}
            onClick={() => handleTabClick(g)}
            className={`px-3 py-1.5 rounded font-semibold text-sm transition-colors ${
              activeGatewayId === g.id && !isAddingNew
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {g.display_name || g.gateway_name}
          </button>
        ))}
        <button
          onClick={handleAddNewClick}
          className={`px-3 py-1.5 rounded font-semibold text-sm transition-colors ${
            isAddingNew
              ? 'bg-green-600 text-white font-bold'
              : 'bg-slate-800 text-green-400 hover:bg-slate-700 font-bold border border-green-600/30'
          }`}
        >
          + Add New Gateway
        </button>
      </div>

      {/* Gateway Pane */}
      {editData && (
        <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-300">Gateway Type</label>
                <select
                  className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white"
                  value={editData.gateway_name || ''}
                  onChange={(e) => setEditData({ ...editData, gateway_name: e.target.value })}
                >
                  <option value="manual_upi_wallet">Manual UPI / Wallet Address</option>
                  <option value="manual_bank">Manual Bank Details</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-300">Display Name</label>
                <input 
                  type="text" 
                  className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" 
                  value={editData.display_name || ''} 
                  onChange={(e) => setEditData({...editData, display_name: e.target.value})} 
                  placeholder="e.g. Bank Card, UPI or USDT Wallet" 
                  required 
                />
              </div>

              {/* ── Currency Dropdown with Checkboxes ── */}
              <div className="md:col-span-2 relative w-1/2" ref={dropdownRef}>
                <label className="block mb-1.5 text-sm font-semibold text-gray-300">Currencies</label>

                <button
                  type="button"
                  onClick={() => setDropdownOpen(o => !o)}
                  className="w-full flex items-center justify-between p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white hover:border-indigo-500 transition-colors"
                >
                  <span className="flex flex-wrap gap-1.5 flex-1 text-left">
                    {selectedCurrencies.length === 0 ? (
                      <span className="text-gray-500">Select currencies...</span>
                    ) : (
                      selectedCurrencies.map(code => {
                        const cur = allCurrencies.find(c => c.code === code);
                        return (
                          <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-600 text-white text-xs font-bold">
                            <span className={`w-1.5 h-1.5 rounded-full ${cur?.currency_type === 'crypto' ? 'bg-yellow-300' : 'bg-green-300'}`} />
                            {code}
                          </span>
                        );
                      })
                    )}
                  </span>
                  <svg className={`w-4 h-4 ml-2 flex-shrink-0 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full max-w-md bg-slate-800 border border-slate-650 rounded-lg shadow-xl overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-700 bg-slate-850">
                      <input
                        type="text"
                        placeholder="Search currency by code or name..."
                        value={curSearch}
                        onChange={(e) => setCurSearch(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {allCurrencies.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-500">No currencies found. Add currencies in Settings first.</p>
                    ) : (
                      (() => {
                        const filtered = allCurrencies.filter(cur => 
                          (cur.code || '').toLowerCase().includes(curSearch.toLowerCase()) || 
                          (cur.name || '').toLowerCase().includes(curSearch.toLowerCase())
                        );
                        if (filtered.length === 0) {
                          return <p className="px-4 py-3 text-sm text-gray-500">No matching currencies found.</p>;
                        }
                        return (
                          <ul className="max-h-56 overflow-y-auto divide-y divide-slate-700" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {filtered.map(cur => {
                              const isSelected = selectedCurrencies.includes(cur.code);
                              return (
                                <li key={cur.id}>
                                  <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-700 transition-colors">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleCurrency(cur.code)}
                                      className="w-4 h-4 accent-indigo-500 rounded"
                                    />
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cur.currency_type === 'crypto' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                                    <span className="font-semibold text-sm text-white">{cur.code}</span>
                                    <span className="text-xs text-gray-400">{cur.name}</span>
                                    <span className="ml-auto text-xs text-gray-500 capitalize">{cur.currency_type || 'fiat'}</span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        );
                      })()
                    )}
                    <div className="px-4 py-2 border-t border-slate-700 flex justify-between items-center bg-slate-850">
                      <span className="text-xs text-gray-500">🟡 Crypto &nbsp; 🟢 Fiat</span>
                      <button type="button" onClick={() => { setDropdownOpen(false); setCurSearch(''); }} className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold">Done</button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-300">Min Amount</label>
                <input 
                  type="number" 
                  className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" 
                  value={editData.min_amount || 0} 
                  onChange={(e) => setEditData({...editData, min_amount: e.target.value})} 
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-300">Max Amount</label>
                <input 
                  type="number" 
                  className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" 
                  value={editData.max_amount || 0} 
                  onChange={(e) => setEditData({...editData, max_amount: e.target.value})} 
                />
              </div>

              {/* ── Custom Fields Configurations ── */}
              <div className="md:col-span-2">
                <h4 className="text-sm font-bold text-indigo-400 mt-2 mb-3">User Input Form Fields Configuration</h4>
                <div className="space-y-2 mb-4 bg-slate-950 p-4 rounded-lg border border-slate-800">
                  {getCustomFields().map((field, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-800 p-2.5 rounded border border-slate-700">
                      <div>
                        <span className="font-semibold text-sm text-white">{field.label}</span>
                        <span className="text-xs text-gray-400 ml-2">({field.key} &bull; type: {field.type || 'text'})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const current = getCustomFields();
                          const updated = current.filter((_, i) => i !== idx);
                          setCustomFields(updated);
                        }}
                        className="text-xs font-bold text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {getCustomFields().length === 0 && (
                    <p className="text-xs text-gray-500 py-2">No fields configured. Users won't be prompted for details. Add at least one field.</p>
                  )}
                </div>

                {/* Add new custom field */}
                <div className="flex gap-2 items-center bg-slate-900/60 p-3 rounded-lg border border-slate-850">
                  <input
                    type="text"
                    id="new-field-label"
                    placeholder="Field Label (e.g. Branch Name)"
                    className="flex-1 p-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const btn = document.getElementById('add-field-btn');
                        if (btn) btn.click();
                      }
                    }}
                  />
                  <select
                    id="new-field-type"
                    className="p-2 bg-slate-800 border border-slate-700 rounded text-sm text-white w-32"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                  </select>
                  <button
                    type="button"
                    id="add-field-btn"
                    onClick={() => {
                      const input = document.getElementById('new-field-label') as HTMLInputElement;
                      const typeSelect = document.getElementById('new-field-type') as HTMLSelectElement;
                      if (!input || !input.value.trim() || !typeSelect) return;
                      
                      const label = input.value.trim();
                      const type = typeSelect.value;
                      const key = label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/^_+|_+$/g, '');
                      
                      const current = getCustomFields();
                      if (current.some(f => f.key === key)) {
                        toast.error('A field with this key/name already exists.');
                        return;
                      }

                      setCustomFields([...current, { label, key, type }]);
                      input.value = '';
                      typeSelect.value = 'text';
                    }}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-750 rounded text-xs font-bold text-white transition-colors"
                  >
                    + Add Field
                  </button>
                </div>
              </div>
              
              <div className="md:col-span-2 mt-4">
                <h4 className="text-md font-bold mb-3 border-b border-gray-700 pb-2">Media & Instructions</h4>
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold text-gray-300">Gateway Logo</label>
                <div className="flex items-center gap-3">
                  <input 
                    type="file" 
                    className="w-full p-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm" 
                    onChange={(e) => { if(e.target.files) setEditData({...editData, logo: e.target.files[0]}) }} 
                  />
                  {editData.logo_url && typeof editData.logo === 'undefined' && (
                    <img src={`${appConfig.baseURL}${editData.logo_url}`} alt="Logo" className="h-10 w-10 object-cover rounded bg-white p-0.5" />
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block mb-1 text-sm font-semibold text-gray-300">Withdrawal Instructions (Visible to User)</label>
                <textarea 
                  className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white h-24" 
                  value={editData.instructions || ''} 
                  onChange={(e) => setEditData({...editData, instructions: e.target.value})} 
                  placeholder="Enter withdrawal instructions..."
                />
              </div>

              <div className="md:col-span-2 flex items-center gap-4 mt-2">
                <span className="text-sm font-semibold text-gray-300">Status (Enable/Disable)</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={editData.status === true} 
                    onChange={(e) => setEditData({...editData, status: e.target.checked})} 
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-gray-700">
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-bold shadow-lg transition-all"
              >
                <i className="fas fa-save mr-2"></i> {isAddingNew ? 'Create Gateway' : 'Save Config'}
              </button>

              {!isAddingNew && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg font-bold shadow-lg transition-all ml-auto"
                >
                  <i className="fas fa-trash-alt mr-2"></i> Delete Gateway
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
